import { useState, useEffect, useCallback, useRef } from "react";
import { db } from '../firebaseConfig';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { GripVertical } from "lucide-react";

const TIME_BANDS = [
  {
    key: "morning",
    label: "Morning",
    icon: "☀",
    placeholder: "How do you begin your day in this role?",
  },
  {
    key: "afternoon",
    label: "Afternoon",
    icon: "◑",
    placeholder: "What sustains you through the middle of the day?",
  },
  {
    key: "evening",
    label: "Evening",
    icon: "☽",
    placeholder: "How do you close the day with intention?",
  },
];

const ACCENT_COLORS = [
  { border: "var(--color-primary)",   dot: "var(--color-primary)" },
  { border: "var(--color-secondary)", dot: "var(--color-secondary)" },
  { border: "var(--color-primary)",   dot: "var(--color-secondary)" },
  { border: "var(--color-secondary)", dot: "var(--color-primary)" },
  { border: "var(--color-primary)",   dot: "var(--color-primary)" },
];

// Injected once — keeps the responsive grid rule out of JS style objects
const RESPONSIVE_CSS = `
  .opp-bands-wrap {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }
  @media (max-width: 600px) {
    .opp-bands-wrap {
      grid-template-columns: 1fr;
    }
  }
`;

export default function RolesRoutinesView({ user }) {
  const [roles, setRoles]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const dragItemId                  = useRef(null);

  const rolesRef = useCallback(
    () => collection(db, "users", user.uid, "roles"),
    [user.uid]
  );

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const q = query(rolesRef(), orderBy("sortOrder", "asc"));
        const snap = await getDocs(q);
        setRoles(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Error loading roles:", e);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRoles();
  }, [rolesRef]);

  const addRole = async () => {
    try {
      const newRole = {
        title: "",
        description: "",
        morning: "",
        afternoon: "",
        evening: "",
        sortOrder: roles.length,
        createdAt: new Date(),
      };
      const ref = await addDoc(rolesRef(), newRole);
      setRoles((prev) => [...prev, { id: ref.id, ...newRole }]);
    } catch (e) {
      console.error("Error adding role:", e);
    }
  };

  const updateRoleField = (roleId, field, value) => {
    setRoles((prev) =>
      prev.map((r) => (r.id === roleId ? { ...r, [field]: value } : r))
    );
  };

  const saveRoleField = async (roleId, field, value) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "users", user.uid, "roles", roleId), {
        [field]: value,
      });
    } catch (e) {
      console.error("Error saving role field:", e);
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (roleId) => {
    if (!window.confirm("Remove this role?")) return;
    try {
      await deleteDoc(doc(db, "users", user.uid, "roles", roleId));
      setRoles((prev) => prev.filter((r) => r.id !== roleId));
    } catch (e) {
      console.error("Error deleting role:", e);
    }
  };

  // ── Drag-and-drop ─────────────────────────────────────────────────────────
  const handleDragStart = (id) => { dragItemId.current = id; };
  const handleDragOver  = (id) => { setDragOverId(id); };
  const handleDrop      = (targetId) => {
    if (dragItemId.current && dragItemId.current !== targetId) {
      reorderRoles(dragItemId.current, targetId);
    }
    setDragOverId(null);
  };
  const handleDragEnd = () => {
    dragItemId.current = null;
    setDragOverId(null);
  };

  const reorderRoles = async (draggedId, targetId) => {
    const draggedIndex = roles.findIndex((r) => r.id === draggedId);
    const targetIndex  = roles.findIndex((r) => r.id === targetId);
    if (draggedIndex === -1 || targetIndex === -1) return;

    const reordered = [...roles];
    const [removed] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, removed);

    setRoles(reordered);

    try {
      await Promise.all(
        reordered.map((role, index) =>
          updateDoc(doc(db, "users", user.uid, "roles", role.id), {
            sortOrder: index,
          })
        )
      );
    } catch (e) {
      console.error("Error saving role order:", e);
      const q = query(rolesRef(), orderBy("sortOrder", "asc"));
      const snap = await getDocs(q);
      setRoles(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <p style={styles.loadingText}>Loading your roles…</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Inject responsive CSS once */}
      <style>{RESPONSIVE_CSS}</style>

      {/* ── Page Header ── */}
      <div style={styles.headerBlock}>
        <h2 style={styles.pageTitle}>Roles &amp; Routines</h2>
        <p style={styles.pageSubtitle}>
          Consider the roles you hold in life — and the daily rhythms that help
          you show up fully in each of them. These are not tasks to complete,
          but intentions to return to.
        </p>
        <div style={styles.compassLine} />
      </div>

      {/* ── Role Cards ── */}
      {roles.length === 0 && (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>
            You haven't defined any roles yet. Add your first one below.
          </p>
        </div>
      )}

      <div style={styles.cardsGrid}>
        {roles.map((role, index) => {
          const accent = ACCENT_COLORS[index % ACCENT_COLORS.length];
          return (
            <RoleCard
              key={role.id}
              role={role}
              accent={accent}
              onUpdate={updateRoleField}
              onSave={saveRoleField}
              onDelete={deleteRole}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              isDragOver={dragOverId === role.id}
            />
          );
        })}
      </div>

      {/* ── Add Role Button ── */}
      <div style={styles.addWrap}>
        <button style={styles.addButton} onClick={addRole}>
          <span style={styles.addIcon}>+</span>
          Add a Role
        </button>
      </div>

      {saving && <div style={styles.savingPill}>Saving…</div>}
    </div>
  );
}

function RoleCard({
  role,
  accent,
  onUpdate,
  onSave,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragOver,
}) {
  return (
    <div
      draggable
      onDragStart={() => onDragStart(role.id)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(role.id); }}
      onDrop={() => onDrop(role.id)}
      onDragEnd={onDragEnd}
      style={{
        ...styles.card,
        borderTopColor: accent.border,
        ...(isDragOver ? styles.cardDragOver : {}),
      }}
    >
      {/* Title row with grip handle */}
      <div style={styles.roleTitleRow}>
        <GripVertical size={16} style={styles.gripHandle} title="Drag to reorder" />
        <span style={{ ...styles.roleDot, backgroundColor: accent.dot }} />
        <input
          type="text"
          value={role.title}
          placeholder="Name this role…"
          style={styles.roleTitleInput}
          onChange={(e) => onUpdate(role.id, "title", e.target.value)}
          onBlur={(e) => onSave(role.id, "title", e.target.value)}
        />
        <button
          style={styles.deleteBtn}
          onClick={() => onDelete(role.id)}
          title="Remove role"
        >
          ×
        </button>
      </div>

      {/* Role Description */}
      <textarea
        value={role.description}
        placeholder="What does this role mean to you? What does it ask of you?"
        style={styles.descTextarea}
        rows={2}
        onChange={(e) => onUpdate(role.id, "description", e.target.value)}
        onBlur={(e) => onSave(role.id, "description", e.target.value)}
      />

      {/* Time Bands — className drives the responsive grid (see RESPONSIVE_CSS) */}
      <div className="opp-bands-wrap">
        {TIME_BANDS.map((band, i) => (
          <div key={band.key} style={styles.band}>
            <div style={styles.bandHeader}>
              <span style={styles.bandIcon}>{band.icon}</span>
              <span style={styles.bandLabel}>{band.label}</span>
              {i < TIME_BANDS.length - 1 && <span style={styles.bandDivider} />}
            </div>
            <textarea
              value={role[band.key] || ""}
              placeholder={band.placeholder}
              style={styles.bandTextarea}
              rows={3}
              onChange={(e) => onUpdate(role.id, band.key, e.target.value)}
              onBlur={(e) => onSave(role.id, band.key, e.target.value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  page: {
    maxWidth: 860,
    margin: "0 auto",
    padding: "2rem 1.25rem 4rem",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "4rem",
  },
  loadingText: {
    color: "var(--color-text-secondary, #888)",
    fontStyle: "italic",
  },
  headerBlock: {
    marginBottom: "2.5rem",
  },
  pageTitle: {
    fontSize: "1.75rem",
    fontWeight: 600,
    color: "var(--color-primary)",
    margin: "0 0 0.75rem",
    letterSpacing: "-0.01em",
  },
  pageSubtitle: {
    fontSize: "1rem",
    lineHeight: 1.75,
    color: "var(--color-text-secondary, #555)",
    fontStyle: "italic",
    margin: "0 0 1.25rem",
    maxWidth: 640,
  },
  compassLine: {
    height: 1,
    background: "linear-gradient(to right, var(--color-primary), transparent)",
    opacity: 0.35,
    width: "100%",
  },
  emptyState: {
    padding: "2rem",
    textAlign: "center",
    marginBottom: "1.5rem",
  },
  emptyText: {
    color: "var(--color-text-secondary, #888)",
    fontStyle: "italic",
    fontSize: "0.95rem",
  },
  cardsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "1.75rem",
  },
  card: {
    background: "var(--color-card-bg, #fff)",
    border: "1px solid var(--color-border, #e0ddd5)",
    borderTop: "4px solid var(--color-primary)",
    borderRadius: 10,
    padding: "1.5rem",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    cursor: "grab",
    transition: "box-shadow 0.15s, transform 0.15s",
  },
  cardDragOver: {
    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
    transform: "scale(1.01)",
    borderColor: "var(--color-primary)",
  },
  roleTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    marginBottom: "0.75rem",
  },
  gripHandle: {
    color: "var(--color-text-secondary, #bbb)",
    cursor: "grab",
    flexShrink: 0,
    opacity: 0.5,
  },
  roleDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
    opacity: 0.85,
  },
  roleTitleInput: {
    flex: 1,
    fontSize: "1.15rem",
    fontWeight: 600,
    fontFamily: "Georgia, 'Times New Roman', serif",
    color: "var(--color-text, #2a2a2a)",
    background: "transparent",
    border: "none",
    borderBottom: "1px dashed var(--color-border, #ccc)",
    outline: "none",
    padding: "0.2rem 0",
    letterSpacing: "0.01em",
    cursor: "text",
  },
  deleteBtn: {
    background: "transparent",
    border: "none",
    color: "var(--color-text-secondary, #aaa)",
    fontSize: "1.25rem",
    cursor: "pointer",
    lineHeight: 1,
    padding: "0 0.25rem",
    opacity: 0.5,
  },
  descTextarea: {
    width: "100%",
    background: "var(--color-input-bg, #faf9f7)",
    border: "1px solid var(--color-border, #e0ddd5)",
    borderRadius: 6,
    padding: "0.65rem 0.85rem",
    fontSize: "0.9rem",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontStyle: "italic",
    color: "var(--color-text, #2a2a2a)",
    lineHeight: 1.65,
    resize: "none",
    outline: "none",
    boxSizing: "border-box",
    marginBottom: "1.25rem",
  },
  // bandsWrap removed — layout is handled by .opp-bands-wrap CSS class above
  band: {
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
  },
  bandHeader: {
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
    marginBottom: "0.1rem",
  },
  bandIcon: {
    fontSize: "0.9rem",
    opacity: 0.75,
    lineHeight: 1,
  },
  bandLabel: {
    fontSize: "0.72rem",
    fontFamily: "system-ui, sans-serif",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "var(--color-text-secondary, #777)",
  },
  bandDivider: {
    flex: 1,
    height: 1,
    background: "var(--color-border, #e0ddd5)",
    opacity: 0.6,
  },
  bandTextarea: {
    flex: 1,
    background: "var(--color-input-bg, #faf9f7)",
    border: "1px solid var(--color-border, #e0ddd5)",
    borderRadius: 6,
    padding: "0.6rem 0.75rem",
    fontSize: "0.875rem",
    fontFamily: "Georgia, 'Times New Roman', serif",
    color: "var(--color-text, #2a2a2a)",
    lineHeight: 1.6,
    resize: "none",
    outline: "none",
    boxSizing: "border-box",
  },
  addWrap: {
    display: "flex",
    justifyContent: "center",
    marginTop: "2rem",
  },
  addButton: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    background: "transparent",
    border: "1px dashed var(--color-primary)",
    borderRadius: 8,
    padding: "0.7rem 1.75rem",
    color: "var(--color-primary)",
    fontSize: "0.95rem",
    fontFamily: "system-ui, sans-serif",
    cursor: "pointer",
    letterSpacing: "0.02em",
    opacity: 0.8,
  },
  addIcon: {
    fontSize: "1.2rem",
    lineHeight: 1,
    fontWeight: 300,
  },
  savingPill: {
    position: "fixed",
    bottom: "1.5rem",
    right: "1.5rem",
    background: "var(--color-primary)",
    color: "#fff",
    fontSize: "0.8rem",
    padding: "0.35rem 0.85rem",
    borderRadius: 20,
    opacity: 0.85,
    fontFamily: "system-ui, sans-serif",
    pointerEvents: "none",
  },
};
